import { AttendanceBalancePool, AttendanceEnumsPoolTypeEnum } from 'generated/resources/graphql'
import { computed, makeObservable } from 'mobx'

import {
  AttendanceCompensationsType,
  CompensationCollection,
} from '../../hooks/useFetchCompensations'
import { UniqueTimeBlockData } from './UniqueTimeBlockData'

const DEFAULT_MINUTES = 60

type CategoryObject = {
  categoryId: number | null | undefined
  equivalentMinutes: number
  rawMinutes: number
  name: string
}

export class BalancePool {
  transfers: UniqueTimeBlockData[]
  usages: UniqueTimeBlockData[]
  type: AttendanceEnumsPoolTypeEnum
  compensations: CompensationCollection | undefined

  constructor(
    balancePool?: AttendanceBalancePool,
    compensations: CompensationCollection | undefined = undefined
  ) {
    this.transfers =
      balancePool?.transfers.map((transfer) => UniqueTimeBlockData.fromAttendanceBlock(transfer)) ||
      []
    this.usages =
      balancePool?.usages.map((usage) => UniqueTimeBlockData.fromAttendanceBlock(usage)) || []
    this.type = balancePool?.type || 'balance'
    this.compensations = compensations

    makeObservable(this, {
      pendingAndCompensated: computed,
      transfersCategories: computed,
      usagesCategories: computed,
      compensationCategories: computed,
      categoryIds: computed,
    })
  }

  setCompensations(compensations: CompensationCollection) {
    this.compensations = compensations
  }

  static empty(type: AttendanceEnumsPoolTypeEnum): BalancePool {
    return new BalancePool({
      transfers: [],
      usages: [],
      type: type,
    })
  }

  get isVirtualPayroll(): boolean {
    return this.type === 'virtual_payroll'
  }

  get totalTransferMinutes(): number {
    return this.transfers.reduce((total, transfer) => total + transfer.minutes, 0)
  }

  get totalUsageMinutes(): number {
    return this.usages.reduce((total, usage) => total + usage.minutes, 0)
  }

  get discardedUsageMinutes(): number {
    return this.usages.reduce(
      (total, usage) => total + (usage.isDiscardedSource ? usage.minutes : 0),
      0
    )
  }

  transfersForCategory(timeRangeCategory: CategoryObject): UniqueTimeBlockData[] {
    return this.transfers.filter(
      (transfer) =>
        transfer.timeSettingsCustomTimeRangeCategoryId === timeRangeCategory.categoryId &&
        transfer.equivalentMinutes === timeRangeCategory.equivalentMinutes &&
        transfer.rawMinutes === timeRangeCategory.rawMinutes
    )
  }

  usagesForCategory(timeRangeCategory: CategoryObject): UniqueTimeBlockData[] {
    return this.usages.filter(
      (usage) =>
        usage.timeSettingsCustomTimeRangeCategoryId === timeRangeCategory.categoryId &&
        usage.equivalentMinutes === timeRangeCategory.equivalentMinutes &&
        usage.rawMinutes === timeRangeCategory.rawMinutes
    )
  }

  compensatedForCategory(timeRangeCategory: CategoryObject): AttendanceCompensationsType {
    return (
      this.compensations?.forCategory({
        id: timeRangeCategory.categoryId,
        equivalentMinutes: timeRangeCategory.equivalentMinutes,
        rawMinutes: timeRangeCategory.rawMinutes,
      }).compensations ?? []
    )
  }

  compensatedForBalance(): AttendanceCompensationsType {
    return (
      this.compensations?.forCategory({
        id: null,
        equivalentMinutes: DEFAULT_MINUTES,
        rawMinutes: DEFAULT_MINUTES,
      }).compensations ?? []
    )
  }

  calculatePendingAndCompensated = (
    transfers: UniqueTimeBlockData[],
    compensations: AttendanceCompensationsType
  ) => {
    const totalTransferMinutes = transfers.reduce((total, transfer) => total + transfer.minutes, 0)
    const totalCompensatedMinutes = compensations.reduce(
      (total, compensation) => total + compensation.minutes,
      0
    )
    const pendingMinutes = Math.max(totalTransferMinutes - totalCompensatedMinutes, 0)
    const compensatedMinutes = Math.max(totalTransferMinutes - pendingMinutes, 0)

    return {
      pendingMinutes,
      compensatedMinutes,
    }
  }

  get transfersCategories(): CategoryObject[] {
    return this.transfers.map((transfer) => ({
      categoryId: transfer.timeSettingsCustomTimeRangeCategoryId,
      equivalentMinutes: transfer.equivalentMinutes,
      rawMinutes: transfer.rawMinutes,
      name: transfer.name,
    }))
  }

  get usagesCategories(): CategoryObject[] {
    return this.usages.map((usage) => ({
      categoryId: usage.timeSettingsCustomTimeRangeCategoryId,
      equivalentMinutes: usage.equivalentMinutes,
      rawMinutes: usage.rawMinutes,
      name: usage.name,
    }))
  }

  get compensationCategories(): CategoryObject[] {
    return (
      this.compensations?.compensations?.map((compensation) => ({
        categoryId: compensation.timeSettingsCustomTimeRangeCategoryId,
        equivalentMinutes: compensation.equivalentMinutesInCents / 100,
        rawMinutes: compensation.rawMinutesInCents / 100,
        name: '',
      })) ?? []
    )
  }

  get categoryIds(): CategoryObject[] {
    const combined = [...this.transfersCategories, ...this.compensationCategories]
    return this.getUniqueArrays(combined)
  }

  pendingAndCompensatedForCategory(timeRangeCategory: CategoryObject) {
    const transfers = this.transfersForCategory(timeRangeCategory)
    const compensationsCategory = this.compensatedForCategory(timeRangeCategory)
    const equivalentMinutes = timeRangeCategory?.equivalentMinutes
    const rawMinutes = timeRangeCategory?.rawMinutes
    const name = timeRangeCategory?.name

    const { pendingMinutes, compensatedMinutes } = this.calculatePendingAndCompensated(
      transfers,
      compensationsCategory
    )

    return {
      pendingMinutes,
      compensatedMinutes,
      equivalentMinutes,
      rawMinutes,
      name,
    }
  }

  get balancePoolBlocks() {
    const pendingBlocks: UniqueTimeBlockData[] = []
    const compensatedBlocks: UniqueTimeBlockData[] = []
    const totalTransferMinutes = this.transfers.reduce(
      (total, transfer) => total + transfer.minutes,
      0
    )
    const totalUsageMinutes = this.usages.reduce((total, usage) => total + usage.minutes, 0)
    const compensationsCategory = this.compensatedForBalance()
    const totalCompensatedMinutes = compensationsCategory.reduce(
      (total, compensation) => total + compensation.minutes,
      0
    )
    const pendingMinutes = Math.max(
      totalTransferMinutes - totalUsageMinutes - totalCompensatedMinutes,
      0
    )
    const compensatedMinutes = Math.max(totalCompensatedMinutes, 0)

    if (pendingMinutes > 0)
      pendingBlocks.push(
        new UniqueTimeBlockData(
          DEFAULT_MINUTES,
          pendingMinutes,
          '',
          DEFAULT_MINUTES,
          this.type,
          null
        )
      )

    if (compensatedMinutes > 0)
      compensatedBlocks.push(
        new UniqueTimeBlockData(
          DEFAULT_MINUTES,
          compensatedMinutes,
          '',
          DEFAULT_MINUTES,
          this.type,
          null
        )
      )

    return {
      pendingMinutes,
      compensatedMinutes,
      pendingBlocks,
      compensatedBlocks,
    }
  }

  get categoryBlocks() {
    const pendingBlocks: UniqueTimeBlockData[] = []
    const compensatedBlocks: UniqueTimeBlockData[] = []
    this.categoryIds.forEach((timeRangeCategoryIdWithEquivalant) => {
      const {
        pendingMinutes: categoryPendingMinutes,
        compensatedMinutes: categoryCompensatedMinutes,
        equivalentMinutes,
        rawMinutes,
        name,
      } = this.pendingAndCompensatedForCategory(timeRangeCategoryIdWithEquivalant)

      if (categoryPendingMinutes > 0)
        pendingBlocks.push(
          new UniqueTimeBlockData(
            equivalentMinutes,
            categoryPendingMinutes,
            name,
            rawMinutes,
            this.type,
            timeRangeCategoryIdWithEquivalant?.categoryId
          )
        )

      if (categoryCompensatedMinutes > 0)
        compensatedBlocks.push(
          new UniqueTimeBlockData(
            equivalentMinutes,
            categoryCompensatedMinutes,
            name,
            rawMinutes,
            this.type,
            timeRangeCategoryIdWithEquivalant?.categoryId
          )
        )
    })

    return { pendingBlocks, compensatedBlocks }
  }

  get pendingAndCompensated() {
    let pendingBlocks: UniqueTimeBlockData[] = []
    let compensatedBlocks: UniqueTimeBlockData[] = []

    if (this.type != 'balance') {
      const categoryBlocks = this.categoryBlocks
      pendingBlocks = categoryBlocks.pendingBlocks
      compensatedBlocks = categoryBlocks.compensatedBlocks
    } else {
      const poolBlocks = this.balancePoolBlocks
      pendingBlocks = poolBlocks.pendingBlocks
      compensatedBlocks = poolBlocks.compensatedBlocks
    }

    const totalPendingMinutes = pendingBlocks.reduce((sum, block) => sum + block.minutes, 0)
    const totalCompensatedMinutes = compensatedBlocks.reduce((sum, block) => sum + block.minutes, 0)

    return {
      pendingBlocks,
      compensatedBlocks,
      pendingMinutes: totalPendingMinutes,
      compensatedMinutes: totalCompensatedMinutes,
    }
  }

  private getUniqueArrays(objects: CategoryObject[]): CategoryObject[] {
    const unique: CategoryObject[] = []
    for (const obj of objects) {
      if (!unique.some((u) => this.areObjectsEqual(u, obj))) {
        unique.push(obj)
      }
    }
    return unique
  }

  // Helper method to compare two objects for equality
  private areObjectsEqual(a: CategoryObject, b: CategoryObject): boolean {
    return (
      a.categoryId === b.categoryId &&
      a.equivalentMinutes === b.equivalentMinutes &&
      a.rawMinutes === b.rawMinutes
    )
  }
}
